/**
 * CalendarEventCard.EditView — edit mode for calendar event creation actions.
 *
 * Datetime boxes become clickable to open a DateTimeRangePicker; title and
 * location are editable text fields. Registers save/cancel handlers with
 * `useActionEdit` so the parent ActionAccordion shell can drive commit and
 * discard from its own Save/Cancel controls.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { DateTimeBox } from '@/lib/crm/object-cards/DateTimeBox';
import { formatDateTimeCompact } from '@/lib/crm/object-cards/formatDateTime';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import type {
  CalendarEventCreatePayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { useResolvedCalendarSlot } from '../../lib/proposals/hooks/useResolvedCalendarSlot';
import { useActionEdit } from './ActionAccordion';
import { AttendeesInput } from './AttendeesInput';
import { CalendarEventEditPicker } from './CalendarEventEditPicker';
import { useActionPayloadSave } from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

export function CalendarEventEditView({
  action,
  proposalId,
  orgId,
  onUpdate,
}: {
  action: ProposedAction & { payload: CalendarEventCreatePayload };
  proposalId?: number;
  orgId?: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
}) {
  const { setDirty, saveHandlerRef, cancelHandlerRef } = useActionEdit();
  const { currentOrg } = useOrg();
  const { timeFormat } = useOrgTimeFormat();
  const displayTimezone = useDisplayTimezone();
  // Prefer the prop orgId; fall back to the current org from context so we
  // never pass an invalid 0 to EntityBadgeByEmail when the prop is briefly
  // undefined during loading.
  const effectiveOrgId = orgId ?? currentOrg?.id;
  const { savePayload } = useActionPayloadSave({
    orgId: effectiveOrgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });
  const params = action.payload.action_params;
  // Never seed the edit picker from a null-coerced date (see useResolvedCalendarSlot).
  const resolved = useResolvedCalendarSlot(params.start_time, params.end_time);

  // Editable state
  const [title, setTitle] = useState(params.title || '');
  const [startTime, setStartTime] = useState(resolved.startTime);
  const [endTime, setEndTime] = useState(resolved.endTime);
  const [location, setLocation] = useState(params.location || '');
  const [attendees, setAttendees] = useState<string[]>(params.attendees ?? []);

  // Remember the value last seeded from the payload, so a later regression
  // (the payload's stored time reverting to null, e.g. a stale cache write)
  // can be told apart from a value the user edited. Initialize from the value
  // the state was seeded with at mount:
  // when a cached resolution is present on first render, startTime/endTime start
  // non-null while the effect has not run yet, so the ref must already hold that
  // cached value for the fail-closed clearing branch to recognize it on a later
  // read failure.
  const seededStartRef = useRef<string | null>(resolved.startTime);
  const seededEndRef = useRef<string | null>(resolved.endTime);

  // Keep start/end in sync with slot resolution. Seed once resolution completes
  // while the field is still unset (never clobbering a value the user already
  // started editing); and if resolution later regresses to null while the field
  // still holds the seeded value untouched, clear it so the picker can't display
  // or save a stale time we can no longer confirm.
  useEffect(() => {
    if (startTime === null && resolved.startTime !== null) {
      seededStartRef.current = resolved.startTime;
      setStartTime(resolved.startTime);
    } else if (
      resolved.startTime === null &&
      startTime !== null &&
      startTime === seededStartRef.current
    ) {
      seededStartRef.current = null;
      setStartTime(null);
    }
    if (endTime === null && resolved.endTime !== null) {
      seededEndRef.current = resolved.endTime;
      setEndTime(resolved.endTime);
    } else if (
      resolved.endTime === null &&
      endTime !== null &&
      endTime === seededEndRef.current
    ) {
      seededEndRef.current = null;
      setEndTime(null);
    }
  }, [resolved.startTime, resolved.endTime, startTime, endTime]);

  // Track original values for dirty detection
  const originalAttendees = useMemo(
    () => params.attendees ?? [],
    [params.attendees]
  );
  const original = useMemo(
    () => ({
      title: params.title || '',
      startTime: resolved.startTime,
      endTime: resolved.endTime,
      location: params.location || '',
      attendees: originalAttendees,
    }),
    [
      params.title,
      resolved.startTime,
      resolved.endTime,
      params.location,
      originalAttendees,
    ]
  );

  // Dirty detection
  useEffect(() => {
    const attendeesDirty =
      attendees.length !== original.attendees.length ||
      attendees.some((e, i) => e !== original.attendees[i]);
    const dirty =
      title !== original.title ||
      startTime !== original.startTime ||
      endTime !== original.endTime ||
      location !== original.location ||
      attendeesDirty;
    setDirty(dirty);
  }, [title, startTime, endTime, location, attendees, original, setDirty]);

  // Register save handler
  useLayoutEffect(() => {
    saveHandlerRef.current = async () => {
      const updatedPayload = {
        ...action.payload,
        action_params: {
          ...params,
          title,
          start_time: startTime,
          end_time: endTime,
          location: location || undefined,
          attendees: attendees.length > 0 ? attendees : undefined,
        },
      };
      await savePayload(updatedPayload);
    };
    return () => {
      saveHandlerRef.current = null;
    };
  }, [
    action.payload,
    params,
    title,
    startTime,
    endTime,
    location,
    attendees,
    savePayload,
    saveHandlerRef,
  ]);

  // Register cancel handler
  useLayoutEffect(() => {
    cancelHandlerRef.current = () => {
      setTitle(original.title);
      setStartTime(original.startTime);
      setEndTime(original.endTime);
      setLocation(original.location);
      setAttendees(original.attendees);
    };
    return () => {
      cancelHandlerRef.current = null;
    };
  }, [original, cancelHandlerRef]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value),
    []
  );
  const handleLocationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setLocation(e.target.value),
    []
  );

  // Handle datetime range change from picker
  const handleDateTimeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      if (range.start) {
        setStartTime(range.start.toISOString());
      }
      if (range.end) {
        setEndTime(range.end.toISOString());
      }
    },
    []
  );

  const start = formatDateTimeCompact(startTime, displayTimezone, timeFormat);
  const end = formatDateTimeCompact(endTime, displayTimezone, timeFormat);

  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3')}>
      {/* Editable title */}
      <input
        type='text'
        value={title}
        onChange={handleTitleChange}
        aria-label='Event title'
        className='w-full text-sm font-semibold text-fg-primary bg-transparent border-b border-border-secondary focus:border-brand-500 outline-none pb-1 transition-colors'
        placeholder='Event title'
      />

      {/* Clickable datetime boxes with picker */}
      <CalendarEventEditPicker
        startTime={startTime}
        endTime={endTime}
        onChange={handleDateTimeChange}
      >
        <div className='flex items-center gap-2'>
          <DateTimeBox label='Start' date={start.date} time={start.time} />
          <span className='text-fg-tertiary text-sm shrink-0'>&ndash;</span>
          <DateTimeBox label='End' date={end.date} time={end.time} />
        </div>
      </CalendarEventEditPicker>

      {/* Attendees — only renders when we have a valid org (prop or context). */}
      {effectiveOrgId !== undefined && (
        <AttendeesInput
          attendees={attendees}
          onChange={setAttendees}
          orgId={effectiveOrgId}
        />
      )}

      {/* Editable location */}
      <input
        type='text'
        value={location}
        onChange={handleLocationChange}
        aria-label='Location'
        className='w-full text-sm text-fg-secondary bg-transparent border-b border-border-secondary focus:border-brand-500 outline-none pb-1 transition-colors'
        placeholder='Add location (optional)'
      />
    </div>
  );
}
